import qs from "querystring";
import fernet from "fernet";
import secureLocalStorage from "react-secure-storage";
import { client_id, client_secret, reposWebApi } from "./customerAPI";
import {
    FORGOT_PASSWORD_FAIL,
    FORGOT_PASSWORD_SUCCESS,
    GET_OTP_FOR_FP,
    GET_OTP_FOR_FP_ERROR,
    GET_OTP_FOR_LOGIN,
    GET_OTP_FOR_LOGIN_ERROR,
    GET_OTP_FOR_SIGNUP,
    GET_OTP_FOR_SIGNUP_ERROR,
    SESSION_LOGIN,
    SESSION_LOGIN_ERROR,
    SIGN_UP,
    VERIFY_FP_OTP,
    VERIFY_FP_OTP_FAIL,
    VERIFY_SIGNUP_OTP,
    VERIFY_SIGNUP_OTP_FAIL,
    VERIFY_USER,
    logout,
} from "./sessionAction";
import { initializeAnalyticsAuth } from "../utils/analyticsCookies";
import decryption from "../components/encryption/Decryption";
import { ENCRYPTION_KEY } from "../env";
import {
    canAccessDashboard,
    persistStepStatus,
    getStoredStepStatus,
} from "../OnBoardingScreen/user-access";

// Every action here resolves to `{ ok: true, ...data }` or `{ ok: false, message }`
// and never shows UI; the onboarding flows decide what to tell the user.

// Assessment payloads sometimes reuse step_1/step_2 for assessment steps — only
// merge statuses that carry the login/diesel-score shape (or questionnaire flags).
const shouldPersistStepStatus = (stepStatus, fromAssessment) => {
    if (!stepStatus || typeof stepStatus !== "object") {
        return false;
    }
    if (!fromAssessment) {
        return true;
    }
    return (
        "procurement_completed" in stepStatus ||
        "operations_completed" in stepStatus ||
        "onboarding_completed" in stepStatus ||
        "registration_completed" in stepStatus ||
        "step_3_completed" in stepStatus
    );
};

const storeStepStatusFromPayload = (payload, { fromAssessment = false } = {}) => {
    if (shouldPersistStepStatus(payload?.step_status, fromAssessment)) {
        persistStepStatus(payload.step_status);
    }
};

export const USER_EXISTS = {
    yes: "YES",
    no: "NO",
};

export const ONBOARDING_SCAN_KEY = "onboardingScan";

const formHeaders = {
    headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
};

const jsonHeaders = { "Content-Type": "application/json" };

const authHeaders = () => ({
    ...jsonHeaders,
    Authorization: "Bearer " + secureLocalStorage.getItem("access_token"),
});

const errorMessage = (error, fallback) => {
    const body = error?.response?.data;
    return (
        body?.meta?.message ||
        body?.error_description ||
        body?.Authorization ||
        (typeof body?.data === "string" && body.data) ||
        body?.exception ||
        (typeof body?.error === "string" && body.error) ||
        error?.message ||
        fallback
    );
};

const failure = (message) => ({ ok: false, message });

// Stale/invalid session: clear storage and hard-reload landing so mid-flow
// React state cannot keep the user on a protected onboarding step.
const returnToLandingOnUnauthorized = async (dispatch, error) => {
    if (error?.response?.status !== 401) {
        return false;
    }
    await dispatch(logout());
    window.location.replace("/onboarding");
    return true;
};

const isFailedEnvelope = (response, payload) => {
    const envelope = response?.data ?? {};
    const meta = envelope.meta;
    return (
        response?.status !== 200 ||
        payload?.success === false ||
        envelope.success === false ||
        Boolean(meta?.is_error) ||
        (meta?.status != null && Number(meta.status) !== 1000)
    );
};

// Questionnaire/assessment bodies are either flat or wrapped as `{ data, meta }`.
const unwrapPayload = (envelope) => {
    const nested = envelope?.data;
    if (
        nested &&
        typeof nested === "object" &&
        !Array.isArray(nested) &&
        (nested.screen_to_show != null || nested.success != null)
    ) {
        return nested;
    }
    return envelope ?? {};
};

const storeSession = (session) => {
    secureLocalStorage.setItem("user", JSON.stringify(session));
    secureLocalStorage.setItem("userId", JSON.stringify(session?.user_details?.id));
    secureLocalStorage.setItem("access_token", session.access_token);
    secureLocalStorage.setItem("refresh_token", session.refresh_token);
    initializeAnalyticsAuth(session);
};

// Lands the user in the app the same way the legacy password login does.
const enterApp = (dispatch, session) => {
    storeSession(session);
    sessionStorage.removeItem(ONBOARDING_SCAN_KEY);
    localStorage.setItem("is_first", true);
    dispatch({ type: SESSION_LOGIN, payload: session });
    window.location.assign("/dashboard");
};

const postLogin = async (body) => {
    const response = await reposWebApi.post(
        "/customer/login",
        qs.stringify({ ...body, client_id, client_secret, grant_type: "password" }),
        formHeaders,
    );
    if (response?.status !== 200 || !response?.data?.access_token) {
        throw new Error("Login failed");
    }
    return response.data;
};

const countryCodeOf = (countryCode) =>
    String(countryCode || "91").replace(/^\+/, "");

const submitAnswers = (path) => (body) => async (dispatch) => {
    try {
        const response = await reposWebApi.post(path, body, { headers: authHeaders() });
        const envelope = response?.data ?? {};
        const payload = unwrapPayload(envelope);
        const message = payload?.message || envelope.message || envelope.meta?.message;

        if (isFailedEnvelope(response, payload)) {
            return failure(message || "Could not save answers");
        }
        storeStepStatusFromPayload(payload, {
            fromAssessment: path.includes("assessment"),
        });
        return {
            ok: true,
            ...payload,
            message,
            screen_to_show: String(payload?.screen_to_show || "").trim(),
        };
    } catch (error) {
        if (await returnToLandingOnUnauthorized(dispatch, error)) {
            return failure("Session expired");
        }
        return failure(errorMessage(error, "Could not save answers"));
    }
};

// Account lookup

export const checkExistingUser = ({ mobile, countryCode }) => async (dispatch) => {
    try {
        const response = await reposWebApi.get("/v3/check_existing_user", {
            headers: authHeaders(),
            params: { mobile_email: mobile, c_code: countryCode },
        });
        const payload = response?.data?.data;
        dispatch({ type: VERIFY_USER, payload });

        const userExists = String(payload?.user_exists || "").toUpperCase();
        if (userExists !== USER_EXISTS.yes && userExists !== USER_EXISTS.no) {
            return failure("Could not check this account");
        }
        return { ok: true, userExists };
    } catch (error) {
        if (await returnToLandingOnUnauthorized(dispatch, error)) {
            return failure("Session expired");
        }
        return failure(errorMessage(error, "Could not check this account"));
    }
};

// Unified OTP — /send|verify/signup_login_forgot/otpv1 (purpose: signup|login|forgot)

export const OTP_PURPOSE = {
    signup: "signup",
    login: "login",
    forgot: "forgot",
};

const OTP_SENT_FALLBACK = "OTP sent successfully";
const OTP_VERIFIED_FALLBACK = "OTP verified successfully";
const OTP_INCORRECT_FALLBACK = "Incorrect code !";

const otpPlainFields = (body) =>
    (typeof body?.meta?.message === "string" && body.meta.message) ||
    (typeof body?.message === "string" && body.message) ||
    (typeof body?.Authorization === "string" && body.Authorization) ||
    undefined;

const parseDecryptedOtpMessage = (raw) => {
    if (typeof raw !== "string" || !raw.trim()) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === "string" && parsed.trim()) {
            return parsed;
        }
        if (typeof parsed?.data === "string" && parsed.data.trim()) {
            return parsed.data;
        }
        if (typeof parsed?.message === "string" && parsed.message.trim()) {
            return parsed.message;
        }
    } catch {
        // plain string message
    }
    // Ignore binary / failed decrypt noise (C0 controls U+0000–U+0008).
    for (let i = 0; i < raw.length; i += 1) {
        if (raw.charCodeAt(i) <= 0x08) {
            return undefined;
        }
    }
    return raw.trim();
};

const decryptOtpData = (encrypted) => {
    if (typeof encrypted !== "string" || !encrypted) {
        return undefined;
    }

    // AES — same as legacy /signup/otp (`decryption(response).data`).
    try {
        const message = parseDecryptedOtpMessage(decryption({ data: encrypted }));
        if (message) {
            return message;
        }
    } catch {
        // try fernet
    }

    const tryFernet = (secretKey) => {
        if (!secretKey) {
            return undefined;
        }
        try {
            const secret = new fernet.Secret(secretKey);
            const token = new fernet.Token({
                secret,
                token: encrypted,
                ttl: 0,
            });
            return parseDecryptedOtpMessage(token.decode());
        } catch {
            return undefined;
        }
    };

    return (
        tryFernet(ENCRYPTION_KEY)
    );
};

// Fernet tokens / long base64 blobs — never surface these as user-facing text.
const looksLikeCiphertext = (value) =>
    typeof value === "string" &&
    (value.startsWith("gAAAA") ||
        (value.length > 40 &&
            !/\s/.test(value) &&
            /^[A-Za-z0-9+/=_-]+$/.test(value)));

// Prefer decrypted `data` from the OTP response; fall back to plain fields.
// Some 400 bodies put a plaintext error in `data` (legacy signup OTP).
const otpMessageFromPayload = (payload, fallback) => {
    const dataField =
        typeof payload === "string"
            ? payload
            : typeof payload?.data === "string"
              ? payload.data
              : undefined;
    const decrypted = decryptOtpData(dataField);
    if (decrypted) {
        return decrypted;
    }
    const plain = otpPlainFields(payload);
    if (plain) {
        return plain;
    }
    if (dataField && !looksLikeCiphertext(dataField)) {
        return dataField;
    }
    return fallback;
};

const otpMessageFromError = (error, fallback) =>
    otpMessageFromPayload(error?.response?.data, fallback) ||
    // Avoid Axios "Request failed with status code …"
    (error?.response ? fallback : undefined) ||
    fallback;

const isOtpFailure = (response, body) =>
    response?.status !== 200 ||
    body?.success === false ||
    Boolean(body?.meta?.is_error);

const sendOtpV1 =
    ({ purpose, mobile, email, countryCode, startType, errorType }) =>
    async (dispatch) => {
        if (startType) {
            dispatch({ type: startType, payload: false });
        }
        try {
            const body = {
                purpose,
                ccode: countryCodeOf(countryCode),
                ...(email ? { email } : { mobile }),
            };
            const response = await reposWebApi.post(
                "/send/signup_login_forgot/otpv1",
                body,
                { headers: jsonHeaders },
            );
            const payload = response?.data;
            const text = otpMessageFromPayload(payload, OTP_SENT_FALLBACK);

            if (
                isOtpFailure(response, payload) ||
                text === "OTP service failed" ||
                text === "This email already registered"
            ) {
                if (errorType) {
                    dispatch({ type: errorType, payload: true });
                }
                return failure(text || "Could not send the code");
            }

            if (startType) {
                dispatch({ type: startType, payload: true });
            }
            return { ok: true, message: text };
        } catch (error) {
            if (errorType) {
                dispatch({ type: errorType, payload: true });
            }
            return failure(
                otpMessageFromError(error, "Could not send the code"),
            );
        }
    };

const verifyOtpV1 =
    ({ purpose, mobile, email, otp, successType, failType }) =>
    async (dispatch) => {
        try {
            const response = await reposWebApi.post(
                "/verify/signup_login_forgot/otpv1",
                {
                    purpose,
                    otp,
                    ...(email ? { email } : { mobile }),
                },
                { headers: jsonHeaders },
            );
            const payload = response?.data;
            const text = otpMessageFromPayload(payload, OTP_VERIFIED_FALLBACK);

            if (
                isOtpFailure(response, payload) ||
                text === "Wrong OTP" ||
                text === "Invalid OTP" ||
                text === OTP_INCORRECT_FALLBACK
            ) {
                if (failType) {
                    dispatch({ type: failType, payload });
                }
                return failure(text || OTP_INCORRECT_FALLBACK);
            }

            if (successType) {
                dispatch({ type: successType, payload });
            }
            return {
                ok: true,
                ...payload,
                message: text,
            };
        } catch (error) {
            if (failType) {
                dispatch({ type: failType, payload: error });
            }
            return failure(
                otpMessageFromError(error, OTP_INCORRECT_FALLBACK),
            );
        }
    };

const isEmailIdentifier = (value) => !/^\d+$/.test(String(value || ""));

export const sendSignupOtp = ({ mobile, countryCode }) =>
    sendOtpV1({
        purpose: OTP_PURPOSE.signup,
        mobile,
        countryCode,
        startType: GET_OTP_FOR_SIGNUP,
        errorType: GET_OTP_FOR_SIGNUP_ERROR,
    });

export const verifySignupOtp = ({ mobile, otp }) =>
    verifyOtpV1({
        purpose: OTP_PURPOSE.signup,
        mobile,
        otp,
        successType: VERIFY_SIGNUP_OTP,
        failType: VERIFY_SIGNUP_OTP_FAIL,
    });

export const sendSignupEmailOtp = (email) =>
    sendOtpV1({
        purpose: OTP_PURPOSE.signup,
        email,
        countryCode: "91",
        startType: GET_OTP_FOR_SIGNUP,
        errorType: GET_OTP_FOR_SIGNUP_ERROR,
    });

export const verifySignupEmailOtp = ({ email, otp }) =>
    verifyOtpV1({
        purpose: OTP_PURPOSE.signup,
        email,
        otp,
        successType: VERIFY_SIGNUP_OTP,
        failType: VERIFY_SIGNUP_OTP_FAIL,
    });

export const sendLoginOtp = ({ mobile, email, countryCode }) =>
    sendOtpV1({
        purpose: OTP_PURPOSE.login,
        mobile: email ? undefined : mobile,
        email,
        countryCode,
        startType: GET_OTP_FOR_LOGIN,
        errorType: GET_OTP_FOR_LOGIN_ERROR,
    });

export const sendForgotPasswordOtp = ({ mobile, countryCode }) => {
    const viaEmail = isEmailIdentifier(mobile);
    return sendOtpV1({
        purpose: OTP_PURPOSE.forgot,
        mobile: viaEmail ? undefined : mobile,
        email: viaEmail ? mobile : undefined,
        countryCode,
        startType: GET_OTP_FOR_FP,
        errorType: GET_OTP_FOR_FP_ERROR,
    });
};

export const verifyForgotPasswordOtp = ({ mobile, otp }) => {
    const viaEmail = isEmailIdentifier(mobile);
    return verifyOtpV1({
        purpose: OTP_PURPOSE.forgot,
        mobile: viaEmail ? undefined : mobile,
        email: viaEmail ? mobile : undefined,
        otp,
        successType: VERIFY_FP_OTP,
        failType: VERIFY_FP_OTP_FAIL,
    });
};

// Signup has no password step, so the account password is derived from the details.
const signupPassword = (name, mobile) => {
    const raw = String(name || "").replace(/\s+/g, "").slice(0, 3);
    const prefix = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "";
    return `${prefix}@${String(mobile || "").slice(-4)}`;
};

// Signs the user in. Dashboard-ready accounts enter the app; others stay mid-onboarding.
export const createAccount = ({ mobile, name, email }) => async (dispatch) => {
    try {
        const signupResponse = await reposWebApi.post(
            "/customer/signupv2",
            { mobile, name, email },
            { headers: jsonHeaders },
        );
        if (isFailedEnvelope(signupResponse)) {
            throw new Error(signupResponse?.data?.meta?.message || "Signup failed");
        }

        const session = await postLogin({
            username: mobile,
            mobile,
            password: signupPassword(name, mobile),
        });

        if (canAccessDashboard(session?.user_details)) {
            enterApp(dispatch, session);
            return { ok: true, enteredApp: true };
        }

        storeSession(session);
        sessionStorage.setItem(ONBOARDING_SCAN_KEY, "1");
        dispatch({ type: SIGN_UP, payload: signupResponse.data });
        return { ok: true };
    } catch (error) {
        dispatch({ type: SESSION_LOGIN_ERROR, payload: error });
        return failure(errorMessage(error, "Something went wrong..!"));
    }
};

export const submitOnboardingQuestionnaire = submitAnswers(
    "/customer/onboarding/questionnaire/submit",
);

export const submitOnboardingAssessment = submitAnswers(
    "/customer/onboarding/assessment/submit",
);

// Maps `/customer/onboarding/questions` into the DynamicForm questionnaire shape.
export const mapOnboardingQuestions = (questions = []) =>
    questions.map((question) => ({
        id: String(question.id),
        category: question.title || "",
        title: question.question || "",
        required: true,
        type: "single",
        options: (question.choices || []).map((choice) => ({
            value: String(choice.id),
            label: choice.title || "",
        })),
    }));

export const fetchOnboardingQuestions = ({ category, step }) => async (dispatch) => {
    try {
        const response = await reposWebApi.get("/customer/onboarding/questions", {
            headers: authHeaders(),
            params: { category, step },
        });
        const envelope = response?.data ?? {};
        const payload = envelope.data ?? {};
        if (isFailedEnvelope(response, payload)) {
            return failure(
                envelope.meta?.message || "Could not load questions",
            );
        }

        const questions = mapOnboardingQuestions(payload.questions || []);
        if (!questions.length) {
            return failure("No questions available");
        }
        return { ok: true, questions };
    } catch (error) {
        if (await returnToLandingOnUnauthorized(dispatch, error)) {
            return failure("Session expired");
        }
        return failure(errorMessage(error, "Could not load questions"));
    }
};

export const fetchDieselScore = () => async (dispatch) => {
    try {
        const response = await reposWebApi.get("/customer/onboarding/diesel-score", {
            headers: authHeaders(),
        });
        const envelope = response?.data ?? {};
        const payload = envelope.data ?? {};
        if (isFailedEnvelope(response, payload)) {
            return failure(
                envelope.meta?.message || "Could not load diesel score",
            );
        }
        storeStepStatusFromPayload(payload);
        return {
            ok: true,
            ...payload,
            screen_to_show: String(payload?.screen_to_show || "").trim(),
        };
    } catch (error) {
        if (await returnToLandingOnUnauthorized(dispatch, error)) {
            return failure("Session expired");
        }
        return failure(errorMessage(error, "Could not load diesel score"));
    }
};

// Fresh step_status from the server before resume redirects. Diesel-score carries
// the full login-shaped status; on failure keep whatever was last persisted.
// 401 clears the session and reloads landing like other authenticated calls.
let refreshStepStatusInflight = null;

export const refreshStepStatus = () => async (dispatch) => {
    if (refreshStepStatusInflight) {
        return refreshStepStatusInflight;
    }

    refreshStepStatusInflight = (async () => {
        try {
            const response = await reposWebApi.get(
                "/customer/onboarding/diesel-score",
                { headers: authHeaders() },
            );
            const envelope = response?.data ?? {};
            const payload = envelope.data ?? {};
            if (
                !isFailedEnvelope(response, payload) &&
                payload?.step_status
            ) {
                persistStepStatus(payload.step_status);
                return { ok: true, stepStatus: getStoredStepStatus() };
            }
        } catch (error) {
            if (await returnToLandingOnUnauthorized(dispatch, error)) {
                return { ok: false, authFailed: true };
            }
            // Non-auth failure — cached/persisted status is the fallback.
        }
        return { ok: false, stepStatus: getStoredStepStatus() };
    })();

    try {
        return await refreshStepStatusInflight;
    } finally {
        refreshStepStatusInflight = null;
    }
};

// Existing customer: password or OTP login

export const loginWithPassword = ({
    mobile,
    email,
    countryCode,
    password,
}) => async (dispatch) => {
    try {
        const viaEmail = Boolean(email);
        const session = await postLogin({
            mobile: viaEmail ? "" : mobile,
            email: viaEmail ? email : "",
            c_code: countryCode || "91",
            password,
            is_email: viaEmail ? "1" : "0",
            is_username: "0",
        });
        enterApp(dispatch, session);
        return { ok: true };
    } catch (error) {
        dispatch({ type: SESSION_LOGIN_ERROR, payload: error });
        return failure(errorMessage(error, "Invalid password"));
    }
};

// Login OTP — /customer/login verifies the code (no separate verify otpv1 call).
export const loginWithOtp = ({ mobile, email, otp }) => async (dispatch) => {
    const viaEmail = Boolean(email);
    try {
        const session = await postLogin({
            mobile: viaEmail ? "" : mobile,
            email: viaEmail ? email : "",
            otp,
            password: 123,
            is_otp: 1,
            is_email: viaEmail ? "1" : "0",
            is_username: "0",
        });
        enterApp(dispatch, session);
        return { ok: true };
    } catch (error) {
        dispatch({ type: SESSION_LOGIN_ERROR, payload: error });
        return failure(otpMessageFromError(error, OTP_INCORRECT_FALLBACK));
    }
};

export const resetPassword = ({ mobile, password, confirmPassword }) => async (dispatch) => {
    try {
        const response = await reposWebApi.post(
            "/customer/v2forgot/password",
            qs.stringify({
                password,
                confirm_password: confirmPassword,
                mobile_email: mobile,
            }),
        );
        if (response?.status !== 200) {
            dispatch({ type: FORGOT_PASSWORD_FAIL });
            return failure("Could not update the password");
        }
        dispatch({ type: FORGOT_PASSWORD_SUCCESS, payload: response?.data?.data });
        return { ok: true, message: "Password Changed" };
    } catch (error) {
        dispatch({ type: FORGOT_PASSWORD_FAIL, payload: error });
        return failure(errorMessage(error, "Could not update the password"));
    }
};
